import { useState } from 'react'
import { cva } from 'class-variance-authority'
import { MessageCircle, Send, CheckCircle, Check, ChevronLeft } from 'lucide-react'
import { cn } from '@/shared/lib/cn'
import { respondToUserInput, respondToUserInputAnswers } from '@/shared/lib/api'
import type { AskUserQuestion } from '@/shared/types'
import { type StepState, emptyStepState, computeStepAnswer } from './askUserPromptSteps'

interface AskUserPromptProps {
  conversationId: number
  toolUseId: string
  pauseKey: string
  /** Flattened fallback text for old clients — only rendered when `questions` is absent. */
  question: string
  /** Present for the Claude Code / Codex-style stepper card; absent renders the legacy free-text card. */
  questions?: AskUserQuestion[]
  answered: boolean
  /** Question text -> answer text. One entry for the legacy card, one per question for the stepper. */
  answers?: Record<string, string>
  onAnswered: (answers: Record<string, string>) => void
}

const optionVariants = cva(
  'w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-violet-400/50',
  {
    variants: {
      selected: {
        true: 'border-violet-400/60 bg-violet-500/10 text-white',
        false: 'border-white/10 bg-white/5 text-white/80 hover:border-violet-400/30 hover:bg-violet-500/5',
      },
    },
    defaultVariants: { selected: false },
  }
)

function OptionButton({
  label,
  description,
  selected,
  multiSelect,
  disabled,
  onClick,
}: {
  label: string
  description?: string
  selected: boolean
  multiSelect: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={multiSelect ? selected : undefined}
      className={cn(optionVariants({ selected }), 'flex items-start justify-between gap-2')}
    >
      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="font-medium">{label}</span>
        {description && <span className="text-xs text-white/40">{description}</span>}
      </span>
      {multiSelect && selected && <Check size={14} className="flex-shrink-0 text-violet-300 mt-0.5" />}
    </button>
  )
}

export function AskUserPrompt({
  conversationId,
  toolUseId,
  pauseKey,
  question,
  questions,
  answered,
  answers,
  onAnswered,
}: AskUserPromptProps) {
  const hasSteps = (questions?.length ?? 0) > 0
  const stepCount = questions?.length ?? 0

  // Legacy free-text card
  const [legacyValue, setLegacyValue] = useState('')

  // Stepper state
  const [stepIndex, setStepIndex] = useState(0)
  const [stepState, setStepState] = useState<StepState[]>(() =>
    Array.from({ length: stepCount }, emptyStepState)
  )

  // Shared submit state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentQuestion = hasSteps ? questions![stepIndex] : undefined
  const currentStep = stepState[stepIndex] ?? emptyStepState()
  const isMultiSelect = currentQuestion?.multiSelect === true
  const isLastStep = stepIndex === stepCount - 1

  async function handleLegacySubmit() {
    const trimmed = legacyValue.trim()
    if (!trimmed || isSubmitting) return
    setIsSubmitting(true)
    setError(null)
    try {
      await respondToUserInput(conversationId, toolUseId, trimmed)
      onAnswered({ [question]: trimmed })
      setLegacyValue('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit answer')
      setIsSubmitting(false)
    }
  }

  function handleLegacyKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleLegacySubmit()
    }
  }

  async function submit(finalStepState: StepState[]) {
    if (!questions) return
    setIsSubmitting(true)
    setError(null)
    const answersMap: Record<string, string> = {}
    questions.forEach((q, i) => {
      answersMap[q.question] = computeStepAnswer(finalStepState[i] ?? emptyStepState())
    })
    try {
      await respondToUserInputAnswers(conversationId, pauseKey, answersMap)
      onAnswered(answersMap)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit answers')
      setIsSubmitting(false)
    }
  }

  function commitStep(index: number, value: StepState) {
    const nextArray = [...stepState]
    nextArray[index] = value
    setStepState(nextArray)
    if (index < stepCount - 1) {
      setStepIndex(index + 1)
    } else {
      void submit(nextArray)
    }
  }

  function toggleOption(label: string) {
    if (isSubmitting || !currentQuestion) return
    const optionOrder = (currentQuestion.options ?? []).map((o) => o.label)
    const isSelected = currentStep.selectedLabels.includes(label)
    const nextLabels = isSelected
      ? currentStep.selectedLabels.filter((l) => l !== label)
      : optionOrder.filter((l) => l === label || currentStep.selectedLabels.includes(l))
    const nextArray = [...stepState]
    nextArray[stepIndex] = { ...currentStep, selectedLabels: nextLabels }
    setStepState(nextArray)
  }

  function updateOtherText(text: string) {
    const nextArray = [...stepState]
    nextArray[stepIndex] = { ...currentStep, otherText: text }
    setStepState(nextArray)
  }

  function handleNext() {
    if (isSubmitting) return
    const other = currentStep.otherText.trim()
    if (isMultiSelect) {
      if (currentStep.selectedLabels.length === 0 && !other) return
      commitStep(stepIndex, currentStep)
    } else if (other) {
      // Single-select: typed Other replaces any option restored by Back.
      commitStep(stepIndex, { selectedLabels: [], otherText: other })
    }
  }

  function handleBack() {
    if (stepIndex === 0 || isSubmitting) return
    setError(null)
    setStepIndex(stepIndex - 1)
  }

  function handleOtherKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    e.preventDefault()
    handleNext()
  }

  // ── Answered — collapsed card ────────────────────────────────────────────────

  if (answered) {
    if (questions && questions.length > 0) {
      return (
        <div className="my-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 overflow-hidden text-xs max-w-[85%]">
          <div className="flex items-start gap-2 px-3 py-2.5">
            <CheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-400 mt-0.5" />
            <div className="flex-1 min-w-0 space-y-1.5">
              {questions.map((q, i) => (
                <div key={i}>
                  <div className="text-white/50">{q.question}</div>
                  <div className="text-emerald-300/80 font-medium">{'→'} {answers?.[q.question] ?? ''}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }
    return (
      <div className="my-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 overflow-hidden text-xs max-w-[85%]">
        <div className="flex items-start gap-2 px-3 py-2.5">
          <CheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-400 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-white/50 mb-1">{question}</div>
            <div className="text-emerald-300/80 font-medium">You answered: "{answers?.[question] ?? ''}"</div>
          </div>
        </div>
      </div>
    )
  }

  // ── Legacy free-text card (no `questions`) ───────────────────────────────────

  if (!hasSteps || !currentQuestion) {
    return (
      <div className="my-1.5 rounded-lg border border-violet-500/30 bg-violet-500/5 overflow-hidden text-xs max-w-[85%]">
        <div className="flex items-start gap-2 px-3 py-2.5 border-b border-violet-500/10">
          <MessageCircle className="w-4 h-4 flex-shrink-0 text-violet-400 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-violet-300/90 font-medium mb-0.5">Claude needs your input</div>
            <div className="text-white/60">{question}</div>
          </div>
        </div>
        <div className="p-3">
          <textarea
            value={legacyValue}
            onChange={(e) => setLegacyValue(e.target.value)}
            onKeyDown={handleLegacyKeyDown}
            placeholder="Type your answer..."
            disabled={isSubmitting}
            aria-label="Your answer"
            className={cn(
              'w-full px-2.5 py-2 rounded-lg bg-white/5 border border-white/10',
              'text-sm text-white/90 placeholder:text-white/30',
              'focus:outline-none focus:border-violet-400/40 focus:ring-1 focus:ring-violet-400/20',
              'resize-none transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            rows={2}
          />
          {error && (
            <div className="mt-2 text-red-400/80 text-xs">{error}</div>
          )}
          <div className="flex items-center justify-end gap-2 mt-2">
            <div className="text-white/30 text-[10px]">Press Enter to submit</div>
            <button
              type="button"
              onClick={handleLegacySubmit}
              disabled={!legacyValue.trim() || isSubmitting}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md',
                'bg-violet-500 text-white text-xs font-medium',
                'hover:bg-violet-400 transition-colors',
                'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-violet-500'
              )}
            >
              <Send size={12} />
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Stepper card — one question at a time, tap an option to advance ──────────

  const hasOther = currentStep.otherText.trim().length > 0
  const canAdvance = hasOther || (isMultiSelect && currentStep.selectedLabels.length > 0)

  return (
    <div className="my-1.5 rounded-lg border border-violet-500/30 bg-violet-500/5 overflow-hidden text-xs max-w-[85%]">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-violet-500/10">
        <MessageCircle className="w-4 h-4 flex-shrink-0 text-violet-400 mt-0.5" />
        <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
          <div className="text-violet-300/90 font-medium">Claude needs your input</div>
          {stepCount > 1 && (
            <div className="text-white/40 text-[10px] flex-shrink-0">
              {stepIndex + 1} of {stepCount}
            </div>
          )}
        </div>
      </div>
      <div className="p-3" aria-live="polite">
        {currentQuestion.header && (
          <span className="inline-block mb-1.5 px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 text-[10px] font-medium uppercase tracking-wide">
            {currentQuestion.header}
          </span>
        )}
        <div className="text-white/70 text-sm mb-2">{currentQuestion.question}</div>
        <div className="flex flex-col gap-1.5">
          {currentQuestion.options?.map((opt) => (
            <OptionButton
              key={opt.label}
              label={opt.label}
              description={opt.description}
              selected={currentStep.selectedLabels.includes(opt.label)}
              multiSelect={isMultiSelect}
              disabled={isSubmitting}
              onClick={() =>
                isMultiSelect
                  ? toggleOption(opt.label)
                  : commitStep(stepIndex, { selectedLabels: [opt.label], otherText: '' })
              }
            />
          ))}
        </div>
        <input
          type="text"
          value={currentStep.otherText}
          onChange={(e) => updateOtherText(e.target.value)}
          onKeyDown={handleOtherKeyDown}
          placeholder="Other…"
          disabled={isSubmitting}
          aria-label="Other"
          className={cn(
            'mt-2 w-full px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10',
            'text-sm text-white/90 placeholder:text-white/30',
            'focus:outline-none focus:border-violet-400/40 focus:ring-1 focus:ring-violet-400/20',
            'transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        />
        {error && <div className="mt-2 text-red-400/80 text-xs">{error}</div>}
        <div className="flex items-center justify-between mt-2">
          {stepIndex > 0 ? (
            <button
              type="button"
              onClick={handleBack}
              disabled={isSubmitting}
              className="inline-flex items-center gap-1 text-white/40 text-[11px] hover:text-white/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={12} /> Back
            </button>
          ) : (
            <span />
          )}
          {(isMultiSelect || hasOther) && (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canAdvance || isSubmitting}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md',
                'bg-violet-500 text-white text-xs font-medium',
                'hover:bg-violet-400 transition-colors',
                'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-violet-500'
              )}
            >
              {isSubmitting ? 'Sending...' : isLastStep ? 'Submit' : 'Next'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
